import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useQuery, useLazyQuery } from '@apollo/client';
import moment from 'moment';
import {
    Pane,
    Card,
    Text,
    Button,
    Heading,
    Spinner,
    Table,
    Alert,
    toaster,
    Strong,
    Badge,
    TextInputField,
    Paragraph,
    Select,
    CaretUpIcon,
    CaretDownIcon,
    Link as EvergreenLink,
    SelectField
} from 'evergreen-ui';
import { CSVLink } from "react-csv";
import { QUERY_PRODUCTS } from '../../graphql/queries/Products/QueryProducts';
import { ForeignKeyMultiSelectDropDownInput } from '../../inputs/ForeignKeyInputs';
import { formatCurrency } from '../../util';
import { generateProfitLossPDF } from './ProfitLossReport';
import { gql } from '@apollo/client';
import { Link as RouterLink, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useApolloClient } from '@apollo/client';
import { Line, Bar } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    BarElement,
} from 'chart.js';
import ProfitLossReport from './ProfitLoss/ProfitLossReport';
import TopProductsReport from './TopProducts/TopProductsReport';
import TimePeriodSelector from './components/TimePeriodSelector';
import DashboardReport from './Dashboard/DashboardReport';

const QUERY_PROFIT_LOSS = gql`
  query ProfitLoss($publisherId: Int!, $startDate: String!, $endDate: String!, $productIds: [Int]) {
    profitLoss(
      publisherId: $publisherId,
      startDate: $startDate,
      endDate: $endDate,
      productIds: $productIds,
      dateField: "payment_sent"
    ) {
      revenue
      expenses
      netProfit
      revenueByProduct {
        productId
        title
        authors
        isbn
        formatType
        retailPrice
        units
        revenue
        expenses
        publicationDate
      }
      expensesByCategory
    }
  }
`;

const QUERY_REVENUE_EXPENSES = gql`
  query RevenueExpenses($publisherId: Int!, $timePeriod: String, $startDate: String, $endDate: String) {
    revenueExpenses(
      publisherId: $publisherId,
      timePeriod: $timePeriod,
      startDate: $startDate,
      endDate: $endDate
    ) {
      months
      revenue
      expenses
    }
  }
`;

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  BarElement,
);

const REPORTING_DASHBOARD_QUERY = gql`
  query ReportingDashboard($publisherId: Int!, $reportType: String!, $timePeriod: String!, $productIds: [Int]) {
    reportingDashboard(
      publisherId: $publisherId,
      reportType: $reportType,
      timePeriod: $timePeriod,
      productIds: $productIds
    )
  }
`;

function groupExpensesByCategory(expenses) {
    return expenses.reduce((groups, expenseJson) => {
        try {
            const expense = JSON.parse(expenseJson);
            const category = expense.category || 'Uncategorized';
            if (!groups[category]) {
                groups[category] = [];
            }
            groups[category].push(expense);
            return groups;
        } catch (e) {
            console.error('Error parsing expense:', e);
            return groups;
        }
    }, {});
}

function RevenueExpensesChart({ data }) {
  const chartData = {
    labels: data.months,
    datasets: [
      {
        label: 'Revenue',
        data: data.revenue,
        borderColor: 'rgb(75, 192, 192)',
        backgroundColor: 'rgba(75, 192, 192, 0.5)',
      },
      {
        label: 'Expenses',
        data: data.expenses,
        borderColor: 'rgb(255, 99, 132)',
        backgroundColor: 'rgba(255, 99, 132, 0.5)',
      },
    ],
  };

  const options = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Revenue & Expenses Over Time',
      },
    },
  };

  return <Line options={options} data={chartData} />;
}

// Add this function to process chart data
const processChartData = (data) => {
  if (!data?.revenueExpenses) return null;

  return {
    months: data.revenueExpenses.months || [],
    revenue: data.revenueExpenses.revenue || [],
    expenses: data.revenueExpenses.expenses || []
  };
};

function Reporting() {
    const client = useApolloClient();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const isProfitLossRoute = location.pathname === '/reporting/profit-loss';
    const currentpublisher = useSelector((state) => state.currentpublisher.value);

    // Get the publisher's currency from Redux store
    const publisherGlobalData = useSelector((state) => state.publisherGlobalData.value);
    const currencySymbol = publisherGlobalData.currency?.symbol || '$';

    // Helper function to format currency with the publisher's currency symbol
    const formatCurrencyWithSymbol = (amount) => {
        return formatCurrency(amount, 'USD', currencySymbol);
    };

    // Initialize dates
    const initializeDates = () => {
        const today = moment();
        const startOfQuarter = today.clone().startOf('quarter');
        return {
            start: startOfQuarter.format('YYYY-MM-DD'),
            end: today.format('YYYY-MM-DD')
        };
    };

    // State declarations
    const [startDate, setStartDate] = useState(initializeDates().start);
    const [endDate, setEndDate] = useState(initializeDates().end);
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [selectedTimePeriod, setSelectedTimePeriod] = useState('Quarter to date');
    const [selectedPeriod, setSelectedPeriod] = useState('custom');
    const [selectedReportType, setSelectedReportType] = useState("Profit & Loss");
    const [isGenerating, setIsGenerating] = useState(false);
    const [details, setDetails] = useState({});
    const [reportData, setReportData] = useState(null);
    const [tableData, setTableData] = useState([]);
    const [revenue, setRevenue] = useState(0);
    const [expenses, setExpenses] = useState(0);
    const [netProfit, setNetProfit] = useState(0);
    const [expenseCategories, setExpenseCategories] = useState({});
    const [data, setData] = useState(null);
    const [localData, setLocalData] = useState(null);
    const [chartData, setChartData] = useState(null);
    const [error, setError] = useState(null);
    // Add dateFilter state
    const [dateFilter, setDateFilter] = useState(new Date().toISOString().split('T')[0]);
    const [sortConfig, setSortConfig] = useState({
        key: null,
        direction: 'ascending'
    });

    const periods = [
        { label: 'Custom', value: 'custom' },
        { label: 'Month to date', value: 'mtd' },
        { label: 'Quarter to date', value: 'qtd' },
        { label: 'Last Quarter', value: 'lastq' },
        { label: 'Year to date', value: 'ytd' },
        { label: 'Last Year', value: 'lasty' }
    ];

    const calculateDates = (period) => {
        const today = moment();
        let start, end;

        switch (period) {
            case 'mtd':
                start = today.clone().startOf('month');
                end = today;
                break;
            case 'qtd':
                start = today.clone().startOf('quarter');
                end = today;
                break;
            case 'lastq':
                start = today.clone().subtract(1, 'quarter').startOf('quarter');
                end = today.clone().subtract(1, 'quarter').endOf('quarter');
                break;
            case 'ytd':
                start = today.clone().startOf('year');
                end = today;
                break;
            case 'lasty':
                start = today.clone().subtract(1, 'year').startOf('year');
                end = today.clone().subtract(1, 'year').endOf('year');
                break;
            default:
                return; // Don't update dates for custom period
        }

        setStartDate(start.format('YYYY-MM-DD'));
        setEndDate(end.format('YYYY-MM-DD'));
    };

    const handlePeriodChange = (period) => {
        setSelectedPeriod(period);
        calculateDates(period);
    };

    // Update the getDateRangeFromPeriod function to handle dates correctly
    const getDateRangeFromPeriod = (periodCode) => {
        const today = moment().startOf('day');  // Start at beginning of current day
        let start, end;

        switch (periodCode) {
            case 'qtd':
                start = today.clone().startOf('quarter');
                end = today.clone().endOf('day');  // End at end of current day
                break;
            case 'mtd':
                start = today.clone().startOf('month');
                end = today.clone().endOf('day');
                break;
            case 'ytd':
                start = today.clone().startOf('year');
                end = today.clone().endOf('day');
                break;
            case 'lastq':
                start = today.clone().subtract(1, 'quarter').startOf('quarter');
                end = today.clone().subtract(1, 'quarter').endOf('quarter');
                break;
            case 'lasty':
                start = today.clone().subtract(1, 'year').startOf('year');
                end = today.clone().subtract(1, 'year').endOf('year');
                break;
            default:
                start = today.clone().startOf('quarter');
                end = today.clone().endOf('day');
        }

        // Format with timezone info
        return {
            start: start.format('YYYY-MM-DD'),
            end: end.format('YYYY-MM-DD')
        };
    };

    // Query for products dropdown - Move this up
    const { loading: productsLoading, data: productsData } = useQuery(QUERY_PRODUCTS, {
        variables: {
            publisher: String(currentpublisher?.id),
            page: 1,
            params: "{}"
        },
        skip: !currentpublisher?.id
    });

    // Query for profit/loss report - Move this up too
    const [getProfitLoss, { loading: queryLoading, error: queryError, data: queryData }] = useLazyQuery(QUERY_PROFIT_LOSS, {
        fetchPolicy: 'network-only'
    });

    // Modify the handleUrlParams function to ensure dates are set correctly
    const handleUrlParams = async () => {
        if (isProfitLossRoute && currentpublisher?.id) {
            const params = new URLSearchParams(location.search);
            const periodParam = params.get('period');
            const productsParam = params.get('products');

            console.log('Processing URL params:', { periodParam, productsParam });

            // Get date range first
            const dateRange = getDateRangeFromPeriod(periodParam || 'qtd');
            setStartDate(dateRange.start);
            setEndDate(dateRange.end);
            setSelectedPeriod(periodParam || 'qtd');

            // Handle product selection
            if (productsParam && productsData?.products) {
                const productId = parseInt(productsParam);
                const selectedProduct = productsData.products.find(p => p.id === productId);

                if (selectedProduct) {
                    const formattedProduct = {
                        id: selectedProduct.id.toString(),
                        key: selectedProduct.id.toString(),
                        value: selectedProduct.title,
                        label: `${selectedProduct.title} ${selectedProduct.isbn ? `(${selectedProduct.isbn})` : ''}`,
                        description: selectedProduct.isbn || ''
                    };

                    setSelectedProducts([formattedProduct]);

                    // Use the same date range for the query
                    await getProfitLoss({
                        variables: {
                            publisherId: String(currentpublisher.id),
                            productIds: [productId],
                            startDate: dateRange.start,
                            endDate: dateRange.end
                        },
                        fetchPolicy: 'network-only'
                    });
                }
            }
        }
    };

    // Also update the handleProductChange function
    const handleProductChange = ({ key, val, exclude }) => {
        const products = val || [];

        // Check if "ALL" is being selected or deselected
        const hasAll = products.some(p => p.id === 'all');
        const hadAll = selectedProducts.some(p => p.id === 'all');

        let newSelection;
        if (hasAll && !hadAll) {
            newSelection = [{
                id: 'all',
                key: 'all',
                value: '- ALL -',
                label: '- ALL -'
            }];
        } else if (!hasAll && hadAll) {
            newSelection = products.filter(p => p.id !== 'all');
        } else {
            newSelection = products.filter(p => p.id !== 'all');
        }

        // Update the selection state
        setSelectedProducts(newSelection);

        // Get current period from URL or default to 'qtd'
        const params = new URLSearchParams(location.search);
        const periodParam = params.get('period') || 'qtd';
        const dateRange = getDateRangeFromPeriod(periodParam);

        // Update URL
        if (products.length === 1 && products[0].id !== 'all') {
            params.set('products', products[0].id);
            params.set('period', periodParam);
            navigate(`${location.pathname}?${params.toString()}`, { replace: true });
        }

        // Trigger query with correct dates
        const productIds = products.some(p => p.id === 'all')
            ? null
            : products.map(p => parseInt(p.id));

        getProfitLoss({
            variables: {
                publisherId: String(currentpublisher.id),
                productIds,
                startDate: dateRange.start,
                endDate: dateRange.end
            },
            fetchPolicy: 'network-only'
        });
    };

    // Debug products data
    useEffect(() => {
        if (productsData?.products) {
            console.log('Products loaded:', productsData.products.length, 'products');
        }
    }, [productsData]);

    useEffect(() => {
        if (queryData && queryData.profitLoss) {
            console.log('Profit Loss Data:', queryData.profitLoss);
            const { revenue, expenses, netProfit, revenueByProduct, expensesByCategory, units } = queryData.profitLoss;

            // Format revenue by product data for the table
            const revenueData = revenueByProduct ? revenueByProduct.map((item, index) => ({
                id: index + 1,
                title: item.title || 'Unknown Book',
                quantity: units ? units[item.productId] || 0 : 0,
                revenue: formatCurrencyWithSymbol(item.revenue || 0),
            })) : [];

            setTableData(revenueData);
            setRevenue(revenue || 0);
            setExpenses(expenses || 0);
            setNetProfit(netProfit || 0);

            // Parse expenses by category
            if (expensesByCategory) {
                try {
                    const parsedExpenses = typeof expensesByCategory === 'string'
                        ? JSON.parse(expensesByCategory)
                        : expensesByCategory;
                    setExpenseCategories(parsedExpenses);
                } catch (e) {
                    console.error('Error parsing expense categories:', e);
                    setExpenseCategories({});
                }
            }
        }
    }, [queryData]);

    useEffect(() => {
        if (queryData) {
            setLocalData(queryData);
        }
    }, [queryData]);

    // Update the query hook to handle both time period and custom dates
    const { loading: chartLoading, error: chartError, data: revenueExpensesData } = useQuery(QUERY_REVENUE_EXPENSES, {
        variables: {
            publisherId: String(currentpublisher?.id),
            timePeriod: selectedTimePeriod !== 'custom' ? selectedTimePeriod : null,
            startDate: selectedTimePeriod === 'custom' ? moment(startDate).format('YYYY-MM-DD') : null,
            endDate: selectedTimePeriod === 'custom' ? moment(endDate).format('YYYY-MM-DD') : null
        },
        skip: !currentpublisher?.id,
        onError: (error) => {
            console.error('Revenue/Expenses Query Error:', error);
        },
        onCompleted: (data) => {
            console.log('Revenue/Expenses Query Result:', data);
        }
    });

    // Add detailed debug logging for parameters
    useEffect(() => {
        console.log('RevenueExpenses Query Debug:', {
            publisherId: parseInt(currentpublisher?.id),
            selectedTimePeriod,
            timePeriod: selectedTimePeriod !== 'custom' ? selectedTimePeriod : null,
            startDate,
            endDate,
            formattedStartDate: selectedTimePeriod === 'custom' ? moment(startDate).format('YYYY-MM-DD') : null,
            formattedEndDate: selectedTimePeriod === 'custom' ? moment(endDate).format('YYYY-MM-DD') : null,
            isCustomPeriod: selectedTimePeriod === 'custom',
            currentRevenueExpensesData: revenueExpensesData
        });
    }, [currentpublisher?.id, selectedTimePeriod, startDate, endDate, revenueExpensesData]);

    // Update the chart data processing
    const timeSeriesChartData = React.useMemo(() => {
        // Add validation - if data is missing or invalid, return empty chart data
        if (!revenueExpensesData?.revenueExpenses ||
            !Array.isArray(revenueExpensesData.revenueExpenses.months) ||
            !Array.isArray(revenueExpensesData.revenueExpenses.revenue) ||
            !Array.isArray(revenueExpensesData.revenueExpenses.expenses)) {
            console.warn('Invalid or missing revenue/expenses data for chart', revenueExpensesData);
            return {
                labels: [],
                datasets: [
                    {
                        label: 'Revenue',
                        data: [],
                        borderColor: 'rgb(75, 192, 192)',
                        backgroundColor: 'rgba(75, 192, 192, 0.5)',
                        tension: 0.3
                    },
                    {
                        label: 'Expenses',
                        data: [],
                        borderColor: 'rgb(255, 99, 132)',
                        backgroundColor: 'rgba(255, 99, 132, 0.5)',
                        tension: 0.3
                    }
                ]
            };
        }

        // Ensure we only show data for valid months
        const currentDate = new Date();
        const data = revenueExpensesData.revenueExpenses;

        // Validate array lengths match to prevent index mismatch errors
        if (data.months.length !== data.revenue.length || data.months.length !== data.expenses.length) {
            console.warn('Data array length mismatch', {
                monthsLength: data.months.length,
                revenueLength: data.revenue.length,
                expensesLength: data.expenses.length
            });
        }

        // Filter out future months and validate data points
        const validMonths = [];
        const validRevenue = [];
        const validExpenses = [];

        // Process each data point with validation
        for (let i = 0; i < data.months.length; i++) {
            // Skip if month data is missing
            if (!data.months[i]) continue;

            const monthDate = new Date(data.months[i]);

            // Skip future months and invalid dates
            if (isNaN(monthDate) || monthDate > currentDate) continue;

            // Add the month
            validMonths.push(data.months[i]);

            // Validate and add revenue/expense data
            validRevenue.push(Number(data.revenue[i] || 0));
            validExpenses.push(Number(data.expenses[i] || 0));
        }

        return {
            labels: validMonths,
            datasets: [
                {
                    label: 'Revenue',
                    data: validRevenue,
                    borderColor: 'rgb(75, 192, 192)',
                    backgroundColor: 'rgba(75, 192, 192, 0.5)',
                    tension: 0.3
                },
                {
                    label: 'Expenses',
                    data: validExpenses,
                    borderColor: 'rgb(255, 99, 132)',
                    backgroundColor: 'rgba(255, 99, 132, 0.5)',
                    tension: 0.3
                }
            ]
        };
    }, [revenueExpensesData]);

    // Add the chart options configuration
    const timeSeriesChartOptions = React.useMemo(() => ({
        responsive: true,
        plugins: {
            legend: {
                position: 'top',
            },
            title: {
                display: true,
            }
        },
        scales: {
            y: {
                beginAtZero: true,
                ticks: {
                    callback: (value) => formatCurrencyWithSymbol(value),
                }
            },
            x: {
                ticks: {
                    callback: (value) => {
                        // Format the date to show MMM YYYY (e.g., "Nov 2024")
                        const date = timeSeriesChartData?.labels?.[value];
                        if (date) {
                            return moment(date).format('MMM YYYY');
                        }
                        return '';
                    }
                }
            }
        }
    }), [timeSeriesChartData?.labels, currencySymbol]); // Re-create when labels or currency symbol changes

    const handleSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    const getSortedData = (data) => {
        if (!sortConfig.key || !data?.profitLoss?.revenueByProduct) return data?.profitLoss?.revenueByProduct;

        return [...data.profitLoss.revenueByProduct].sort((a, b) => {
            if (a[sortConfig.key] === null) return 1;
            if (b[sortConfig.key] === null) return -1;

            let aVal = a[sortConfig.key];
            let bVal = b[sortConfig.key];

            // Handle numeric values
            if (['revenue', 'expenses', 'retailPrice', 'units'].includes(sortConfig.key)) {
                aVal = parseFloat(aVal) || 0;
                bVal = parseFloat(bVal) || 0;
            }

            if (aVal < bVal) {
                return sortConfig.direction === 'ascending' ? -1 : 1;
            }
            if (aVal > bVal) {
                return sortConfig.direction === 'ascending' ? 1 : -1;
            }
            return 0;
        });
    };

    const columns = [
        {
            Header: 'Title',
            accessor: 'title',
        },
        {
            Header: 'Quantity Sold',
            accessor: 'quantity',
        },
        {
            Header: 'Revenue',
            accessor: 'revenue',
        },
    ];

    const generateReport = () => {
        setIsGenerating(true);

        // Validate dates
        if (!startDate || !endDate) {
            toaster.danger('Please select both start and end dates');
            setIsGenerating(false);
            return;
        }

        // Validate publisher
        if (!currentpublisher?.id) {
            toaster.danger('Please select a publisher');
            setIsGenerating(false);
            return;
        }

        console.log('Current selected products:', selectedProducts);

        // Filter out null/undefined values and convert to integers
        const validProductIds = selectedProducts
            .filter(product => product && product.id)
            .map(product => parseInt(product.id))
            .filter(id => !isNaN(id));

        console.log('Valid product IDs:', validProductIds);

        // If no products are selected, pass null to get all products
        const productIdsParam = validProductIds.length > 0 ? validProductIds : null;

        // Navigate to the profit-loss route before generating report
        navigate('/reporting/profit-loss');

        // Execute the query
        getProfitLoss({
            variables: {
                publisherId: String(currentpublisher.id),
                productIds: productIdsParam,
                startDate,
                endDate
            },
            fetchPolicy: 'network-only'
        }).catch(error => {
            console.error('Error generating report:', error);
            toaster.danger('Error generating report: ' + error.message);
            setIsGenerating(false);
        });
    };

    const getCSVData = () => {
        if (!queryData?.profitLoss) return [];

        const rows = [];

        // Header row
        rows.push(['Profit & Loss Report', '', '', '', '', '', '', '']);
        rows.push(['Period:', `${startDate} to ${endDate}`, '', '', '', '', '', '']);
        rows.push(['', '', '', '', '', '', '', '']);

        // Column Headers
        rows.push([
            'Title',
            'ISBN',
            'Type',
            'Retail Price',
            'Quantity Sold',
            'Revenue',
            'Expenses',
            'Profit in period',
            ''
        ]);

        // Product rows
        if (queryData.profitLoss.revenueByProduct) {
            queryData.profitLoss.revenueByProduct.forEach(item => {
                const title = item.title || 'Unknown Book';
                const authors = item.authors && item.authors.length > 0
                    ? ` by ${item.authors.join(', ')}`
                    : '';
                const itemRevenue = item.revenue || 0;
                const itemExpenses = item.expenses || 0;
                const itemProfit = itemRevenue - itemExpenses;

                rows.push([
                    `${title}${authors}`,
                    item.isbn || 'N/A',
                    item.formatType || 'N/A',
                    `${formatCurrencyWithSymbol(item.retailPrice || 0)}`,
                    item.units || 0,
                    `${formatCurrencyWithSymbol(itemRevenue)}`,
                    `${formatCurrencyWithSymbol(itemExpenses)}`,
                    `${formatCurrencyWithSymbol(itemProfit)}`,
                    ''
                ]);
            });
        }

        // Totals row
        rows.push([
            'Totals',
            '-',
            '-',
            '-',
            queryData.profitLoss.units || 0,
            `${formatCurrencyWithSymbol(queryData.profitLoss.revenue || 0)}`,
            `${formatCurrencyWithSymbol(queryData.profitLoss.expenses || 0)}`,
            `${formatCurrencyWithSymbol(queryData.profitLoss.netProfit || 0)}`,
            ''
        ]);

        return rows;
    };

    const validateDate = (dateString) => {
        const date = new Date(dateString);
        const currentDate = new Date();

        // Check if the date is valid and not in the future
        return !isNaN(date.getTime()) && date <= currentDate;
    };

    useEffect(() => {
        if (currentpublisher?.id && dateFilter) {
            // Filter out null/undefined values and convert to integers
            const validProductIds = selectedProducts
                .filter(product => product && product.id)
                .map(product => parseInt(product.id))
                .filter(id => !isNaN(id));

            // If no products are selected, pass null to get all products
            const productIdsParam = validProductIds.length > 0 ? validProductIds : null;

            getProfitLoss({
                variables: {
                    publisherId: String(currentpublisher.id),
                    productIds: productIdsParam,
                    startDate,
                    endDate
                },
                fetchPolicy: 'network-only'
            }).catch(error => {
                console.error('Error generating report:', error);
                toaster.danger('Error generating report: ' + error.message);
            });
        }
    }, [currentpublisher?.id, startDate, endDate, selectedProducts]);

    const { data: dashboardData, loading: dashboardLoading, error: dashboardError, refetch } = useQuery(REPORTING_DASHBOARD_QUERY, {
        variables: {
            publisherId: String(currentpublisher?.id),
            reportType: selectedReportType,
            timePeriod: selectedTimePeriod,
            productIds: []
        },
        skip: !currentpublisher?.id,
        fetchPolicy: 'network-only'
    });

    // Add an effect to refetch when time period changes
    useEffect(() => {
        if (currentpublisher?.id && selectedTimePeriod) {
            refetch();
        }
    }, [selectedTimePeriod, currentpublisher?.id]);

    // Add the number formatter if not already present
    const formatNumber = (num) => {
        return new Intl.NumberFormat('en-US').format(num);
    };

    // Move useEffect to the top with other hooks
    useEffect(() => {
        if (dashboardData?.reportingDashboard) {
            const formattedData = JSON.parse(dashboardData.reportingDashboard);
            if (formattedData?.stats?.top_products) {
                console.log('Top Products Data:', formattedData.stats.top_products);
            }
        }
    }, [dashboardData]);

    // Helper function can stay here
    const getProductId = (product) => {
        // Try the most likely field names based on the naming pattern
        return product.product || product.product_id || product.product__id || product.id;
    };

    // Add a check for the top products route
    const isTopProductsRoute = location.pathname === '/reporting/top-products';

    // Add to your useEffect that handles initial route setup
    useEffect(() => {
        // Set the correct report type based on the current route
        if (location.pathname === '/reporting/profit-loss') {
            setSelectedReportType('Profit & Loss by Product');
        } else if (location.pathname === '/reporting/top-products') {
            setSelectedReportType('Top Products');
        } else if (location.pathname === '/reporting') {
            setSelectedReportType('Profit & Loss');
        }
    }, [location.pathname]);

    // Add this near your other state declarations
    useEffect(() => {
        // Set default selection to "ALL" when products are loaded
        if (productsData?.products && !selectedProducts.length) {
            setSelectedProducts([{
                id: 'all',
                key: 'all',
                value: '- ALL -',
                label: '- ALL -'
            }]);
        }
    }, [productsData]);

    if (dashboardLoading) return <Spinner />;
    if (dashboardError) return (
        <Alert
            intent="danger"
            title="Error loading dashboard data"
            marginBottom={16}
        >
            {dashboardError.message}
        </Alert>
    );

    if (!currentpublisher?.id) return (
        <Alert
            intent="warning"
            title="No Publisher Selected"
            marginBottom={16}
        >
            Please select a publisher to view the dashboard
        </Alert>
    );

    const formattedDashboardData = dashboardData?.reportingDashboard ? JSON.parse(dashboardData.reportingDashboard) : null;

    const chartDataForDashboard = {
        labels: ['Revenue', 'Expenses'],
        datasets: [
            {
                data: [formattedDashboardData?.chart_data?.revenue || 0, formattedDashboardData?.chart_data?.expenses || 0],
                backgroundColor: ['rgba(75, 192, 192, 0.6)', 'rgba(255, 99, 132, 0.6)'],
            },
        ],
    };

    const chartOptionsForDashboard = {
        responsive: true,
        plugins: {
            legend: {
                display: false,
            },
            title: {
                display: false,
            },
        },
        scales: {
            y: {
                beginAtZero: true,
                ticks: {
                    callback: (value) => formatCurrencyWithSymbol(value),
                },
            },
        },
    };

    return (
        <Pane position="absolute" padding={16} width="100%" height="100%" background="#f8f6f4">
            <Pane>
                {/* Common header for all views */}
                <Pane id="page-heading" width="100%" display="flex" alignItems="center" paddingBottom={16}>
                    <Pane display="flex" alignItems="center">
                        <Heading size={500} marginRight={8}>Reports</Heading>

                        <SelectField
                            className="big-select"
                            value={selectedReportType}
                            onChange={(e) => {
                                setSelectedReportType(e.target.value);
                                if (e.target.value === "Profit & Loss by Product") {
                                    navigate('/reporting/profit-loss');
                                } else if (e.target.value === "Top Products") {
                                    navigate('/reporting/top-products');
                                } else if (e.target.value === "Profit & Loss") {
                                    navigate('/reporting');
                                }
                            }}
                            width={300}
                            appearance="minimal"
                            alignItems="flex-start"
                            textAlign="left"

                        >
                            <option value="Profit & Loss" style={{ fontSize: '20px !important', fontWeight: 500, padding: '12px' }}>
                                Profit & Loss
                            </option>
                            <option value="Profit & Loss by Product" style={{ fontSize: '20px', fontWeight: 500, padding: '12px' }}>
                                Profit & Loss by Product
                            </option>
                            <option value="Top Products" style={{ fontSize: '20px', fontWeight: 500, padding: '12px' }}>
                                Top Products
                            </option>
                        </SelectField>
                    </Pane>
                </Pane>

                {/* Time Period Selector for all views except Profit & Loss by Product */}
                {!isProfitLossRoute && (
                    <TimePeriodSelector
                        selectedTimePeriod={selectedTimePeriod}
                        setSelectedTimePeriod={setSelectedTimePeriod}
                        startDate={startDate}
                        endDate={endDate}
                        setStartDate={setStartDate}
                        setEndDate={setEndDate}
                        showCustomDates={true}  // Changed from false to true
                    />
                )}

                {isProfitLossRoute ? (
                    <ProfitLossReport
                        startDate={startDate}
                        endDate={endDate}
                        selectedPeriod={selectedPeriod}
                        selectedProducts={selectedProducts}
                        handlePeriodChange={handlePeriodChange}
                        setStartDate={setStartDate}
                        setEndDate={setEndDate}
                        setSelectedPeriod={setSelectedPeriod}
                        productsLoading={productsLoading}
                        productsData={productsData}
                        handleProductChange={handleProductChange}
                        details={details}
                        setDetails={setDetails}
                        localData={localData}
                        queryLoading={queryLoading}
                        chartLoading={chartLoading}
                        chartError={chartError}
                        chartData={chartData}
                        getCSVData={getCSVData}
                        handleSort={handleSort}
                        sortConfig={sortConfig}
                        getSortedData={getSortedData}
                        revenue={revenue}
                        expenses={expenses}
                        netProfit={netProfit}
                        periods={periods}
                        reportData={queryData?.profitLoss}
                        timePeriodSelector={
                            <TimePeriodSelector
                                selectedTimePeriod={selectedPeriod}
                                setSelectedTimePeriod={handlePeriodChange}  // Use handlePeriodChange here
                                startDate={startDate}
                                endDate={endDate}
                                setStartDate={setStartDate}
                                setEndDate={setEndDate}
                                showCustomDates={true}
                            />
                        }
                    />
                ) : isTopProductsRoute ? (
                    <TopProductsReport
                        selectedTimePeriod={selectedTimePeriod}
                        setSelectedTimePeriod={setSelectedTimePeriod}
                        currentpublisher={currentpublisher}
                        loading={dashboardLoading}
                    />
                ) : (
                    <DashboardReport
                        timeSeriesChartData={timeSeriesChartData}
                        timeSeriesChartOptions={timeSeriesChartOptions}
                        formattedDashboardData={formattedDashboardData}
                        currentpublisher={currentpublisher}
                        getProfitLoss={getProfitLoss}
                        startDate={startDate}
                        endDate={endDate}
                    />
                )}
            </Pane>
        </Pane>
    );
}

export default Reporting;
